import { apiFileUpload, FileItemProps, FolderItemProps } from "apis";
import { CSSProperties, FC, ReactNode, useMemo, useRef, useState } from "react";
import { Fragment } from "react/jsx-runtime"
// import "./AppUpload.scss";
import AppAlert from "./AppAlert";
import Content from "layouts/admin/pages/library/Content";
import { uuid } from "utils";

interface AppUploadImageBaseProps {
    children?: ReactNode;
    height?: number | string;
    width?: number | string;
    minHeight?: number | string;
    remove?: boolean;
    imageClassName?: string;
}
interface AppUploadImageSingleProps extends AppUploadImageBaseProps {
    multiple?: false;
    value?: string;
    onChange?: (url?: string) => void;
}
interface AppUploadImageMultipleProps extends AppUploadImageBaseProps {
    multiple: true;
    value?: string[];
    onChange?: (url: string[]) => void;
}

export type AppUploadImageProps = AppUploadImageSingleProps | AppUploadImageMultipleProps;
type SelectedItemType = { item: FolderItemProps; type: "folder"; } | { item: FileItemProps; type: "file"; }
const AppUploadImage: FC<AppUploadImageProps> = ({
    children,
    multiple = false,
    height,
    width,
    minHeight,
    remove = true,
    imageClassName,
    ...props
}) => {

    const divStyle: CSSProperties = useMemo(() => {

        return {
            minHeight: minHeight || 100,
            width,
            height
        }
    }, [height, width])

    const divClassName: string = useMemo(() => {
        let className = "border border-dashed border-gray-300 hover:border-blue-600 rounded flex justify-center items-center cursor-pointer bg-gray-100";
        return className;
    }, [])

    const [confirmBtnId] = useState(uuid());
    return <Fragment>
        <div
            className={divClassName}
            style={divStyle}
            onClick={() => {
                const modalInstance = AppAlert.modal({
                    title: "Chọn hình ảnh",
                    fullWidth: true,
                    fullHeight: true,
                    content: <Fragment>
                        <FileSelectModalContent
                            {...{
                                children,
                                multiple,
                                height,
                                minHeight,
                                remove,
                                imageClassName,
                                ...props
                            } as AppUploadImageProps}
                            confirmBtnId={confirmBtnId}
                            onClose={() => modalInstance.close()}
                        />
                    </Fragment>,
                    footer: multiple ? <div className="flex justify-end space-x-2 mt-2">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            onClick={() => {
                                document.getElementById(confirmBtnId)?.click();
                            }}
                        >
                            Chọn
                        </button>
                    </div> : undefined
                })
            }}
        >
            {/* {children} */}
            {
                multiple ? <div className="flex flex-wrap gap-3 p-4">
                    {((props.value || []) as string[]).map((url, i) => (
                        <ImagePreview
                            key={url + i}
                            url={url}
                            className={imageClassName}
                            onRemove={
                                remove
                                    ? () => {
                                        props.onChange?.(
                                            ((props.value || []) as string[]).filter(
                                                (_, j) => i !== j
                                            ) as any
                                        );
                                    }
                                    : undefined
                            }
                        />
                    ))}
                </div> :
                    (props.value && <ImagePreview
                        className={imageClassName}
                        url={props.value as string}
                        onRemove={remove ?
                            () => {
                                props.onChange?.(undefined as any);
                            } : undefined}
                    />)
            }
        </div>
    </Fragment>
}
export default AppUploadImage;

interface ImagePreviewProps {
    url: string;
    onRemove?: () => void;
    className?: string;
}
const ImagePreviewBk: FC<ImagePreviewProps> = ({ url, onRemove, className }) => {
    const wrapperClassName = useMemo(() => {
        let _className = "rounded-inherit relative rounded-full group w-full h-full " + (className || "");
        return _className;
    }, [className]);

    const imageClassName = useMemo(() => {
        let _className = "rounded-inherit w-full h-full rounded object-contain ";

        return _className;
    }, [])
    return <span className={wrapperClassName} >
        {/* Nút remove */}
        <span className={`relative inline-block group ${className}`}>
            {onRemove && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove()
                    }}
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow hover:bg-red-600 
                               opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                    ×
                </button>
            )}

            {/* Ảnh */}
            <img
                src={`${process.env.REACT_APP_FILE_URL}${url}`}
                alt={url}
                className={imageClassName}
            />
        </span>
    </span>
}
const ImagePreview: FC<ImagePreviewProps> = ({ url, onRemove, className }) => {

    const wrapperClassName = useMemo(() => {
        return `
            relative group/image-preview
            w-[200px] h-[200px]
            flex-shrink-0
            ${className || ""}
        `;
    }, [className]);

    return (
        <div className={wrapperClassName}>
            {onRemove && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    type="button"
                    className="
                        absolute -top-2 -right-2 z-10
                        bg-red-500 text-white
                        rounded-full w-6 h-6
                        flex items-center justify-center
                        shadow hover:bg-red-600
                        opacity-0 group-hover/image-preview:opacity-100
                        transition-opacity duration-200
                    "
                >
                    ×
                </button>
            )}

            <img
                src={`${process.env.REACT_APP_FILE_URL}${url}`}
                alt={url}
                className="
                    w-full h-full
                    rounded
                    object-cover
                    border border-gray-200
                    bg-white
                "
            />
        </div>
    );
};

type FileSelectModalContentProps = AppUploadImageProps & { onClose?: () => void; confirmBtnId: string; }
const FileSelectModalContent: FC<FileSelectModalContentProps> = ({ multiple, onChange, onClose, value, confirmBtnId }) => {
    const [selecteds, setSelecteds] = useState<SelectedItemType[]>([]);

    return (
        <>
            <Content
                minType={["image"]}
                multiple={multiple}
                allowSelectFolder={false}
                onDoubleClickFile={(file) => {
                    const path = file.path;
                    if (multiple) {
                        onChange?.([...(Array.isArray(value) ? value : []), path] as any)
                    }
                    else {
                        onChange?.(path as any);
                    }
                    onClose?.();
                }}
                handleSelected={(items) => {
                    setSelecteds(items);
                }}
            />
            <button
                id={confirmBtnId}
                hidden
                onClick={() => {
                    const fileSelecteds = selecteds.filter(s => s.type === "file");
                    const paths = fileSelecteds.map(s => (s.item as FileItemProps)?.path) as string[];
                    if (multiple) {
                        onChange?.([...(Array.isArray(value) ? value : []), ...paths] as any)
                    }
                    else {
                        onChange?.(paths[0] as any);
                    }
                    onClose?.();
                }} />


        </>
    );
}